import { Suspense, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { PdfFirstPageLazy } from "@/domains/documents/components/pdf/PdfFirstPage.lazy";
import { useStableCallback } from "@/hooks/useStableCallback";
import { cn } from "@/lib/utils";
import { DocumentBreadcrumbHover } from "../DocumentBreadcrumbHover";
import {
  browserCanShowImage,
  canTransformImage,
  previewWidthStep,
  type AttachmentPreviewKind,
  type AttachmentSource,
  type GalleryPicture,
} from "./attachmentPreview";
import { useAttachmentPreviewUrl } from "./useAttachmentPreviewUrl";

/**
 * The largest width step this tile has needed so far. Never steps back down: a
 * tile shrinking keeps its sharper image instead of fetching a smaller one.
 */
function useWidthStep(width: number, scale: number): number {
  const needed = previewWidthStep(Math.ceil(width * scale));
  const [step, setStep] = useState(needed);
  if (needed > step) setStep(needed);
  return Math.max(step, needed);
}

function devicePixelRatio(): number {
  return typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
}

/**
 * The URL a tile draws from: a local file's own object URL, or a signed
 * Storage URL — resized to `width` when given. The Storage query is always
 * mounted (hooks cannot be conditional) but only enabled for a stored file.
 */
function useSourceUrl(
  source: AttachmentSource,
  width: number | null
): { url: string | null; failed: boolean } {
  const stored = source.type === "stored";
  const signed = useAttachmentPreviewUrl(stored ? source.filePath : "", width, stored);
  return stored ? signed : { url: source.url, failed: false };
}

interface PreviewProps {
  picture: GalleryPicture;
  width: number;
  onAspectRatio: (aspectRatio: number) => void;
  onLoaded: () => void;
  onFailed: () => void;
}

/**
 * An image, resized by Storage when it can be; the original when the resize
 * fails (over the size or pixel limit) and the browser can show the format.
 */
function ImagePreview({ picture, width, onAspectRatio, onLoaded, onFailed }: PreviewProps) {
  // A local file has no resized copy to ask for: the browser shows it as is.
  const [variant, setVariant] = useState<"resized" | "original">(() =>
    picture.source.type === "stored" && canTransformImage(picture) ? "resized" : "original"
  );
  const step = useWidthStep(width, devicePixelRatio());
  const { url, failed } = useSourceUrl(picture.source, variant === "resized" ? step : null);

  const fallBack = useStableCallback(() => {
    if (variant === "resized" && browserCanShowImage(picture)) setVariant("original");
    else onFailed();
  });

  // Signing failed (the image itself failing to load is `onError` below).
  useEffect(() => {
    if (failed) fallBack();
  }, [failed, fallBack]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      draggable={false}
      decoding="async"
      loading="lazy"
      className="h-full w-full object-cover"
      onLoad={(event) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (naturalWidth > 0 && naturalHeight > 0) onAspectRatio(naturalWidth / naturalHeight);
        onLoaded();
      }}
      onError={fallBack}
    />
  );
}

/** A PDF's first page, drawn by pdf.js from the byte ranges it needs. */
function PdfPreview({ picture, width, onAspectRatio, onLoaded, onFailed }: PreviewProps) {
  // react-pdf scales the canvas by devicePixelRatio itself, so step in CSS pixels.
  const step = useWidthStep(width, 1);
  const { url, failed } = useSourceUrl(picture.source, null);
  const fail = useStableCallback(onFailed);

  useEffect(() => {
    if (failed) fail();
  }, [failed, fail]);

  if (!url) return null;
  return (
    // Drawn at the width step, scaled to the tile.
    <div className="h-full w-full">
      <Suspense fallback={null}>
        <PdfFirstPageLazy
          url={url}
          // A local file's object URL is read whole; ranges are for Storage.
          size={picture.source.type === "stored" ? picture.size : null}
          width={step}
          onAspectRatio={onAspectRatio}
          onRendered={onLoaded}
          onError={onFailed}
        />
      </Suspense>
    </div>
  );
}

export interface AttachmentTileProps {
  picture: GalleryPicture;
  kind: AttachmentPreviewKind;
  buildingId: number;
  /** Placement and size, from the gallery's layout. */
  style: CSSProperties;
  /** The tile's width in CSS pixels — what the picture is fetched or drawn for. */
  renderWidth: number;
  /** Opens the full preview; absent when the file cannot be opened. */
  onOpen?: () => void;
  onAspectRatio: (key: string, aspectRatio: number) => void;
  /** No preview could be drawn — the section lists the file as a row instead. */
  onPreviewFailed: (key: string) => void;
  /** Shown while the pipeline is still working on the file. */
  processingLabel?: string | null;
  /** Corner slot, revealed on hover — the remove button. */
  action?: ReactNode;
  /** Covers the tile with "+N", for the attachments the collapsed gallery hides. */
  hiddenCount?: number;
  onShowHidden?: () => void;
  showHiddenLabel?: string;
}

function AttachmentCaption({
  picture,
  buildingId,
}: Pick<AttachmentTileProps, "picture" | "buildingId">) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
      <DocumentBreadcrumbHover segments={picture.breadcrumbSegments} buildingId={buildingId}>
        <span className="pointer-events-auto block truncate text-[11px] font-medium text-white">
          {picture.filename}
        </span>
      </DocumentBreadcrumbHover>
    </div>
  );
}

/**
 * One visual attachment in the issue panel's gallery: the picture itself (an
 * image, or a PDF's first page), sized by the gallery's justified layout. The
 * filename shows on hover and focus; clicking opens the same full preview the
 * attachment rows open.
 */
export function AttachmentTile(props: AttachmentTileProps) {
  const {
    picture,
    kind,
    buildingId,
    style,
    renderWidth,
    onOpen,
    onAspectRatio,
    onPreviewFailed,
    processingLabel,
    action,
    hiddenCount = 0,
    onShowHidden,
    showHiddenLabel,
  } = props;
  const [loaded, setLoaded] = useState(false);
  const Preview = kind === "pdf" ? PdfPreview : ImagePreview;

  return (
    <li className="group overflow-hidden rounded-md border border-border bg-muted" style={style}>
      {!loaded && <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden />}

      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        aria-label={picture.filename}
        title={picture.filename}
        className={cn(
          "absolute inset-0 block overflow-hidden bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          onOpen ? "cursor-zoom-in" : "cursor-default",
          !loaded && "opacity-0"
        )}
      >
        <Preview
          picture={picture}
          width={renderWidth}
          onAspectRatio={(ratio) => onAspectRatio(picture.key, ratio)}
          onLoaded={() => setLoaded(true)}
          onFailed={() => onPreviewFailed(picture.key)}
        />
      </button>

      {/* Filename caption — on hover or keyboard focus, never covering the
          picture at rest. Hovering the name itself shows the folder path. */}
      <AttachmentCaption picture={picture} buildingId={buildingId} />

      {processingLabel && (
        <span className="pointer-events-none absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm">
          <Loader2 className="h-3 w-3 animate-spin" />
          {processingLabel}
        </span>
      )}

      {action && (
        <div className="absolute right-1 top-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {action}
        </div>
      )}

      {hiddenCount > 0 && onShowHidden && (
        <button
          type="button"
          onClick={onShowHidden}
          aria-label={showHiddenLabel}
          className="absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-semibold text-white transition-colors hover:bg-black/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          +{hiddenCount}
        </button>
      )}
    </li>
  );
}
